import React, { useState, useCallback } from "react";
import Modal from "@mui/material/Modal";
import { CloudUpload } from "@mui/icons-material";
import { Download } from "lucide-react";
import { ExcelImportProps, ImportSummary, ImportProgress } from "../../types/UserImport";
import { processExcelImport, generateExcelTemplate } from "../../utils/ExcelImportUtils";
import { getUserIdFromLocalStorage } from "../../API_Endpoints/APIEndpoints";
import { BASE_URL } from "../../App_Settings/app_settings";
import FileUploadSection from "./FileUploadSection";
import ImportPreview from "./ImportPreview";
import ImportProgressComponent from "./ImportProgress";
import "./UserImport.css";

interface UserImportModalProps extends ExcelImportProps {}

const UserImportModal: React.FC<UserImportModalProps> = ({
  show,
  onClose,
  onImportComplete,
  availableRoles,
  availableBusinessUnits,
  availableOrganizations,
  currentUserOrganizationId,
  isSuperAdmin,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState<"upload" | "preview" | "importing" | "complete">(
    "upload"
  );

  const handleClose = useCallback(() => {
    if (!isProcessing) {
      setSelectedFile(null);
      setImportSummary(null);
      setProgress(null);
      setCurrentStep("upload");
      onClose();
    }
  }, [isProcessing, onClose]);

  const handleFileSelect = useCallback(
    async (file: File) => {
      setSelectedFile(file);
      setIsProcessing(true);
      setProgress({
        stage: "parsing",
        current: 0,
        total: 100,
        message: "Parsing Excel file...",
        percentage: 10,
      });

      try {
        const userData = getUserIdFromLocalStorage();
        const summary = await processExcelImport(
          file,
          availableRoles,
          availableBusinessUnits,
          availableOrganizations,
          currentUserOrganizationId || userData.organizationId,
          userData.userName || "System",
          isSuperAdmin
        );

        setImportSummary(summary);
        setCurrentStep("preview");
        setProgress({
          stage: "complete",
          current: 100,
          total: 100,
          message: "File processed successfully",
          percentage: 100,
        });
      } catch (error) {
        console.error("Import processing error:", error);
        alert(`Error processing file: ${error instanceof Error ? error.message : "Unknown error"}`);
        setCurrentStep("upload");
      } finally {
        setIsProcessing(false);
      }
    },
    [
      availableRoles,
      availableBusinessUnits,
      availableOrganizations,
      currentUserOrganizationId,
      isSuperAdmin,
    ]
  );

  const handleDownloadTemplate = useCallback(async () => {
    try {
      const templateBlob = await generateExcelTemplate(
        availableRoles,
        availableBusinessUnits,
        availableOrganizations,
        isSuperAdmin
      );

      const url = URL.createObjectURL(templateBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `user-import-template-${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating template:", error);
      alert("Error generating template file");
    }
  }, [availableRoles, availableBusinessUnits, availableOrganizations, isSuperAdmin]);

  const handleImport = useCallback(async () => {
    if (!importSummary) return;

    const successfulUsers = importSummary.results.filter((r) => r.success && r.processed);
    if (successfulUsers.length === 0) {
      alert("No valid users to import");
      return;
    }

    setIsProcessing(true);
    setCurrentStep("importing");
    setProgress({
      stage: "saving",
      current: 0,
      total: successfulUsers.length,
      message: "Creating users...",
      percentage: 0,
    });

    try {
      const userData = getUserIdFromLocalStorage();
      let successCount = 0;
      let errorCount = 0;

      for (let i = 0; i < successfulUsers.length; i++) {
        const userResult = successfulUsers[i];

        setProgress({
          stage: "saving",
          current: i + 1,
          total: successfulUsers.length,
          message: `Creating user ${i + 1} of ${successfulUsers.length}: ${userResult.user.email}`,
          percentage: Math.round(((i + 1) / successfulUsers.length) * 100),
        });

        try {
          const response = await fetch(`${BASE_URL}/Users`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userData.token}`,
            },
            body: JSON.stringify(userResult.processed),
          });

          if (response.ok) {
            successCount++;
          } else {
            errorCount++;
            console.error(`Failed to create user ${userResult.user.email}:`, response.statusText);
          }
        } catch (error) {
          errorCount++;
          console.error(`Error creating user ${userResult.user.email}:`, error);
        }

        // Small delay to prevent overwhelming the server
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      const finalSummary: ImportSummary = {
        ...importSummary,
        successCount,
        errorCount,
      };

      setProgress({
        stage: "complete",
        current: successfulUsers.length,
        total: successfulUsers.length,
        message:
          errorCount > 0
            ? `Import complete: ${successCount} users created successfully, ${errorCount} failed`
            : `Import complete: ${successCount} users created successfully`,
        percentage: 100,
      });

      setCurrentStep("complete");

      // Call the completion callback after a short delay to show final status
      setTimeout(() => {
        onImportComplete(finalSummary);
        handleClose();
      }, 2000);
    } catch (error) {
      console.error("Import error:", error);
      alert(`Import failed: ${error instanceof Error ? error.message : "Unknown error"}`);
      setCurrentStep("preview");
    } finally {
      setIsProcessing(false);
    }
  }, [importSummary, onImportComplete, handleClose]);

  const handleRetry = useCallback(() => {
    setCurrentStep("upload");
    setSelectedFile(null);
    setImportSummary(null);
    setProgress(null);
  }, []);

  const canImport = importSummary && importSummary.successCount > 0;

  return (
    <Modal open={show} onClose={handleClose}>
      <div className="user-import-modal">
        <div className="user-import-header">
          <h2 className="user-import-title">
            <CloudUpload />
            Import Users from Excel
          </h2>
          <p className="user-import-subtitle">Upload an Excel file to bulk import user accounts</p>
        </div>

        <div className="user-import-content">
          {/* Template Download Section */}
          {!selectedFile && currentStep === "upload" && (
            <div className="template-section">
              <p className="template-text">
                Download the Excel template to ensure your data is in the correct format for import.
              </p>
              <button className="template-button" onClick={handleDownloadTemplate}>
                <Download size={16} />
                Download Template
              </button>
            </div>
          )}

          {/* File Upload Section */}
          {currentStep === "upload" && (
            <FileUploadSection
              onFileSelect={handleFileSelect}
              isProcessing={isProcessing}
              selectedFile={selectedFile}
            />
          )}

          {/* Progress Section */}
          {progress && (currentStep === "preview" || currentStep === "importing") && (
            <ImportProgressComponent progress={progress} />
          )}

          {/* Preview Section */}
          {currentStep === "preview" && importSummary && (
            <ImportPreview summary={importSummary} onRetry={handleRetry} />
          )}

          {/* Complete Section */}
          {currentStep === "complete" && progress && (
            <div className="progress-container">
              <ImportProgressComponent progress={progress} />
            </div>
          )}
        </div>

        {/* Modal Actions */}
        <div className="modal-actions">
          <button
            className="action-button secondary cancel-button"
            onClick={handleClose}
            disabled={isProcessing}
            type="button"
          >
            {/* <Close /> */}
            {currentStep === "complete" ? "Close" : "Cancel"}
          </button>

          {currentStep === "preview" && canImport && (
            <button
              className="action-button primary"
              onClick={handleImport}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <div className="loading-spinner" />
                  Importing...
                </>
              ) : (
                `Import ${importSummary.successCount} Users`
              )}
            </button>
          )}

          {currentStep === "preview" && !canImport && (
            <button className="action-button primary" onClick={handleRetry}>
              Try Another File
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default UserImportModal;
